import React, { useState, useEffect, useCallback } from 'react';
import { 
  loadLocalProfile, 
  saveLocalProfile, 
  loadLocalWorkouts, 
  checkDayRollover, 
  saveTodayWater, 
  saveTodayWorkoutDone 
} from './lib/storage';
import { Profile, Workout } from './types/database.types';
import { Navbar } from './components/layout/Navbar';
import { BottomNav, NavTab } from './components/layout/BottomNav';
import { WaterCircleProgress } from './components/hydration/WaterCircleProgress';
import { WorkoutActiveMode } from './components/workout/WorkoutActiveMode';
import { RecompProgressCard } from './components/dashboard/RecompProgressCard';
import { SettingsView } from './components/settings/SettingsView';
import { applyTheme, getStoredTheme, initThemeListener } from './lib/theme';
import { startNotificationScheduler } from './lib/notificationScheduler';
import { fetchHAWaterMl, syncHAWaterMl } from './lib/homeAssistant';

export function App() {
  const [currentTab, setCurrentTab] = useState<NavTab>(() => {
    if (typeof window !== 'undefined' && window.location.hash === '#workout') {
      return 'workout';
    }
    return 'hydration';
  });
  const [profile, setProfile] = useState<Profile>(loadLocalProfile());
  const [workouts] = useState<Workout[]>(loadLocalWorkouts());
  const [currentWaterMl, setCurrentWaterMl] = useState<number>(0);
  const [workoutDoneToday, setWorkoutDoneToday] = useState(false);
  const [isWorkoutSessionActive, setIsWorkoutSessionActive] = useState(false);
  const [, setThemeVersion] = useState(0);

  const handleAddWater = useCallback((amount: number) => {
    setCurrentWaterMl((prev) => {
      const newTotal = prev + amount;
      saveTodayWater(newTotal);
      syncHAWaterMl(newTotal).catch(() => {});
      return newTotal;
    });
  }, []);

  const [haConnected, setHaConnected] = useState<boolean>(false);

  // Sincronização bidirecional em tempo real com o Home Assistant (botão físico Zigbee, automações, etc.)
  const syncWaterFromHA = useCallback(async () => {
    const haMl = await fetchHAWaterMl();
    if (haMl === null) {
      setHaConnected(false);
      return;
    }

    setHaConnected(true);
    setCurrentWaterMl((prevLocal) => {
      if (haMl !== prevLocal) {
        if (haMl > prevLocal) {
          // Home Assistant tem valor maior (clique no botão físico Zigbee)
          saveTodayWater(haMl);
          return haMl;
        } else {
          // App tem valor maior (bebeu água e registrou no app)
          syncHAWaterMl(prevLocal).catch(() => {});
          return prevLocal;
        }
      }
      return prevLocal;
    });
  }, []);

  // Inicializa tema, persistência local e checagem diária
  useEffect(() => {
    applyTheme(getStoredTheme());
    const cleanupTheme = initThemeListener(() => {
      setThemeVersion((v) => v + 1);
    });

    // Checagem de virada de dia e carregamento dos dados locais
    const dayData = checkDayRollover();
    setCurrentWaterMl(dayData.waterMl);
    setWorkoutDoneToday(dayData.workoutDone);
    setProfile((prev) => ({ ...prev, streak_days: dayData.streakDays }));

    // Sincroniza com o Home Assistant imediatamente ao carregar
    syncWaterFromHA();

    // Sincroniza sempre que o app volta para o primeiro plano (quando o usuário desbloqueia o celular ou troca de aba)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncWaterFromHA();
      }
    };
    const handleFocus = () => {
      syncWaterFromHA();
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Polling contínuo a cada 10s enquanto o app estiver aberto na tela
    const pollInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        syncWaterFromHA();
      }
    }, 10000);

    // Purgar qualquer cache antigo de ações pendentes para nunca auto-avançar doses
    if ('caches' in window) {
      caches.delete('zac-pending-actions').catch(() => {});
    }

    return () => {
      cleanupTheme();
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      clearInterval(pollInterval);
    };
  }, [syncWaterFromHA]);

  // Escuta navegações vindas do Service Worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const handleMessage = (event: MessageEvent) => {
        if (event.data?.type === 'NAVIGATE_TAB') {
          setCurrentTab(event.data.tab || 'hydration');
        }
      };

      navigator.serviceWorker.addEventListener('message', handleMessage);
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      };
    }
  }, []);

  const handleUndoWater = () => {
    setCurrentWaterMl((prev) => {
      const newTotal = Math.max(0, prev - profile.water_dose_ml);
      saveTodayWater(newTotal);
      syncHAWaterMl(newTotal).catch(() => {});
      return newTotal;
    });
  };

  const handleFinishWorkout = (workoutName: string, durationSecs: number, totalVolumeKg: number) => {
    setWorkoutDoneToday(true);
    saveTodayWorkoutDone(true);
    setIsWorkoutSessionActive(false);
    
    const newStreak = profile.streak_days + 1;
    const updatedProfile = { ...profile, streak_days: newStreak };
    setProfile(updatedProfile);
    saveLocalProfile(updatedProfile);

    alert(`⚡ TREINO CONCLUÍDO COM SUCESSO!\n\n${workoutName}\nDuração: ${Math.round(durationSecs / 60)} min\nVolume Total: ${totalVolumeKg.toLocaleString('pt-BR')} kg levantados!\n\nSua disciplina foi registrada no dispositivo.`);
    setCurrentTab('hydration');
  };

  const waterGoalReached = currentWaterMl >= profile.daily_water_target_ml;

  return (
    <div className="min-h-screen bg-[#F2F2F7] dark:bg-black text-slate-900 dark:text-slate-100 flex flex-col font-sans transition-colors duration-300 selection:bg-[#30D6FB] selection:text-black relative overflow-x-hidden">
      {/* Liquid Glass Ambient Refraction Light Orbs (iOS 26-27 Next-Gen Caustics) */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-40 -left-40 w-[28rem] h-[28rem] bg-[#0A84FF]/12 dark:bg-[#0A84FF]/18 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute top-1/4 -right-40 w-[30rem] h-[30rem] bg-[#30D6FB]/10 dark:bg-[#30D6FB]/14 rounded-full blur-[130px]" />
        <div className="absolute -bottom-40 left-1/4 w-[32rem] h-[32rem] bg-[#30D158]/10 dark:bg-[#30D158]/12 rounded-full blur-[140px]" />
        <div className="absolute top-2/3 -left-32 w-80 h-80 bg-[#BF5AF2]/8 dark:bg-[#BF5AF2]/10 rounded-full blur-[110px]" />
      </div>

      {/* Barra de Navegação Superior Minimalista (oculta no modo treino ativo) */}
      {!isWorkoutSessionActive && (
        <div className="relative z-20">
          <Navbar
            streakDays={profile.streak_days}
            onOpenSettings={() => setCurrentTab('settings')}
          />
        </div>
      )}

      {/* Conteúdo Central */}
      <main className={`flex-1 w-full max-w-xl mx-auto relative z-10 ${isWorkoutSessionActive ? 'px-3 py-0' : 'px-4 py-3'}`}>
        {/* Aba 1: Hidratação (1º Card: Horário Limite -> 2º Card: Anel 3D -> 3º Card: Grade 13 Doses) */}
        {currentTab === 'hydration' && (
          <WaterCircleProgress
            currentMl={currentWaterMl}
            targetMl={profile.daily_water_target_ml}
            doseMl={profile.water_dose_ml}
            onAddDose={(amount) => handleAddWater(amount)}
            onUndoDose={handleUndoWater}
            haConnected={haConnected}
            onSyncHA={syncWaterFromHA}
            cutoffTime={profile.workout_cutoff_time}
            workoutDone={workoutDoneToday}
            onNavigateToWorkout={() => setCurrentTab('workout')}
          />
        )}

        {/* Aba 2: Workout Mode (Academia & Descanso Ativo) */}
        {currentTab === 'workout' && (
          <WorkoutActiveMode
            workouts={workouts}
            onFinishWorkout={handleFinishWorkout}
            onWorkoutActiveChange={setIsWorkoutSessionActive}
          />
        )}

        {/* Aba 3: Recomposição (Disciplina Colorida + Durateston + Cockpit -15kg) */}
        {currentTab === 'recomp' && (
          <RecompProgressCard
            profile={profile}
            daysRemaining={53}
            waterGoalReached={waterGoalReached}
            workoutDone={workoutDoneToday}
            onNavigateToWorkout={() => setCurrentTab('workout')}
          />
        )}

        {/* Aba 4: Ajustes (Aba Completa e Nativa, sem Pop-up!) */}
        {currentTab === 'settings' && (
          <SettingsView
            onThemeChanged={() => setThemeVersion((v) => v + 1)}
          />
        )}
      </main>

      {/* Navegação Inferior Fixa (oculta no modo treino ativo) */}
      {!isWorkoutSessionActive && (
        <BottomNav
          currentTab={currentTab}
          onTabChange={(tab) => setCurrentTab(tab)}
          isWorkoutActive={false}
        />
      )}
    </div>
  );
}

export default App;
